<template>
  <div class="lpm-page">

    <!-- ===================================================== -->
    <!-- HERO STRIP (top of modal body)                        -->
    <!-- ===================================================== -->
    <div class="lpm-intro">
      <div class="lpm-intro-text">
        <div class="lpm-intro-label">Layaway payments</div>
        <div class="lpm-intro-title">
          Payment history &amp; new payments
        </div>
      </div>
      <div class="lpm-intro-tags">
        <span class="lpm-tag is-orange">
          <b-icon icon="receipt"></b-icon> Invoice {{ invoice.invoice_id || '—' }}
        </span>
        <span class="lpm-tag is-teal">
          <b-icon icon="cash-stack"></b-icon> ${{ (invoice.total - total_refunded_value).toFixed(2) }} total
        </span>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- EXISTING PAYMENTS SECTION                             -->
    <!-- ===================================================== -->
    <section class="lpm-card">
      <div class="lpm-section-head">
        <div class="lpm-section-title">
          <span class="lpm-icon"><b-icon icon="clock-history"></b-icon></span>
          <div>
            <div class="lpm-section-label">Payment history</div>
            <div class="lpm-section-sub">
              {{ invoice.payment_methods.length }} payment{{ invoice.payment_methods.length === 1 ? '' : 's' }} recorded
            </div>
          </div>
        </div>
      </div>

      <div v-if="invoice.payment_methods.length === 0" class="lpm-empty">
        <div class="lpm-empty-icon"><b-icon icon="credit-card" font-scale="2.4"></b-icon></div>
        <h3>No payments made yet</h3>
        <p>Add the first payment to get started.</p>
      </div>

      <div v-else class="lpm-table-wrap">
        <table class="lpm-table">
          <thead>
            <tr>
              <th>Date &amp; time</th>
              <th>Method</th>
              <th class="lpm-td--num">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ipm, ipmidx) in invoice.payment_methods" :key="ipmidx">
              <td>
                <span class="lpm-date-cell">{{ format_ts(ipm.ts) }}</span>
              </td>
              <td>
                <span class="lpm-method-chip" :class="'is-' + (ipm.value || '').toLowerCase()">
                  {{ (ipm.value || '').toUpperCase() }}
                </span>
                <span v-if="ipm.name && ipm.name !== 'N/A'" class="lpm-method-detail">{{ ipm.name }}</span>
              </td>
              <td class="lpm-td--num">
                <span class="lpm-amount">${{ (parseFloat(ipm.amount) || 0.0).toFixed(2) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary cards -->
      <div class="lpm-summary">
        <div class="lpm-summary-card is-total">
          <div class="lpm-sum-label">Total</div>
          <div class="lpm-sum-value">${{ (invoice.total - total_refunded_value).toFixed(2) }}</div>
        </div>

        <div class="lpm-summary-card is-paid">
          <div class="lpm-sum-label">Paid</div>
          <div class="lpm-sum-value">${{ get_pms_total().toFixed(2) }}</div>
        </div>

        <div class="lpm-summary-card is-current">
          <div class="lpm-sum-label">Current payment</div>
          <div class="lpm-sum-value">${{ get_current_pms_total().toFixed(2) }}</div>
        </div>

        <div class="lpm-summary-card is-balance" :class="{ 'is-positive': ((invoice.total - total_refunded_value) - get_pms_total() - get_current_pms_total()) > 0 }">
          <div class="lpm-sum-label">Remaining balance</div>
          <div class="lpm-sum-value">
            ${{ (((invoice.total - total_refunded_value) - get_pms_total() - get_current_pms_total()) || 0.0).toFixed(2) }}
          </div>
        </div>
      </div>

      <!-- Sales tax alert -->
      <div v-if="sales_tax > 0" class="lpm-tax-alert">
        <span class="lpm-tax-icon"><b-icon icon="cash-coin"></b-icon></span>
        <div>
          <div><strong>SALES TAX:</strong> ${{ sales_tax.toFixed(2) }}</div>
          <div v-if="is_last_payment" class="lpm-tax-note">Added to final payment</div>
        </div>
      </div>
    </section>

    <!-- ===================================================== -->
    <!-- NEW PAYMENTS SECTION                                  -->
    <!-- ===================================================== -->
    <section class="lpm-card">
      <div class="lpm-section-head">
        <div class="lpm-section-title">
          <span class="lpm-icon is-orange"><b-icon icon="plus-circle"></b-icon></span>
          <div>
            <div class="lpm-section-label">Add new payments</div>
            <div class="lpm-section-sub">
              {{ payment_methods.length }} pending
            </div>
          </div>
        </div>
        <b-button class="lpm-btn-primary" @click="add_payment_method">
          <b-icon icon="plus-lg"></b-icon> Add payment
        </b-button>
      </div>

      <!-- Payment cards -->
      <div v-if="payment_methods.length > 0" class="lpm-grid">
        <div
          v-for="(payment_method, pmidx) in payment_methods"
          :key="pmidx"
          class="lpm-pay-card"
          :class="{ 'is-tax': payment_method.layaway_sls_tax }"
        >
          <div class="lpm-pay-head">
            <div class="lpm-pay-num">
              <b-icon icon="credit-card"></b-icon>
              Payment #{{ pmidx + 1 }}
            </div>
            <div class="lpm-pay-head-right">
              <span v-if="payment_method.layaway_sls_tax" class="lpm-chip is-warn">
                <b-icon icon="cash-coin"></b-icon> Sales tax
              </span>
              <b-button class="lpm-remove-btn" @click="removePaymentMethod(pmidx)" title="Remove">
                <b-icon icon="x-lg"></b-icon>
              </b-button>
            </div>
          </div>

          <div class="lpm-pay-body">
            <!-- Method select -->
            <label class="lpm-field">
              <span class="lpm-field-label">Payment method</span>
              <b-form-select
                v-model="payment_methods[pmidx].value"
                :options="payment_method_options"
                size="sm"
                @change="reset_key_metrics"
                class="lpm-select"
              ></b-form-select>
            </label>

            <!-- CASH -->
            <template v-if="payment_methods[pmidx].value == 'cash'">
              <label class="lpm-field">
                <span class="lpm-field-label">Amount</span>
                <b-input
                  size="sm"
                  type="number"
                  v-model="payment_methods[pmidx].amount"
                  @keyup="set_validated(false)"
                  :disabled="payment_method.layaway_sls_tax"
                  placeholder="Enter amount"
                  class="lpm-input"
                ></b-input>
              </label>
            </template>

            <!-- CREDIT CARD -->
            <template v-if="payment_methods[pmidx].value == 'cc'">
              <label class="lpm-field">
                <span class="lpm-field-label">Amount</span>
                <b-input
                  size="sm"
                  type="number"
                  v-model="payment_methods[pmidx].amount"
                  @keyup="set_validated(false)"
                  :disabled="payment_method.layaway_sls_tax"
                  placeholder="Enter amount"
                  class="lpm-input"
                ></b-input>
              </label>

              <label class="lpm-field">
                <span class="lpm-field-label">Last 4 digits</span>
                <b-input
                  size="sm"
                  v-model="payment_methods[pmidx].name"
                  @keyup="set_validated(false)"
                  maxlength="4"
                  placeholder="••••"
                  class="lpm-input"
                ></b-input>
              </label>
            </template>

            <!-- FINANCE -->
            <template v-if="payment_methods[pmidx].value == 'finance'">
              <label class="lpm-field">
                <span class="lpm-field-label">Company</span>
                <b-form-select
                  v-model="payment_methods[pmidx].name"
                  :options="finance_companies"
                  @change="set_validated(false)"
                  size="sm"
                  class="lpm-select"
                ></b-form-select>
              </label>

              <template v-if="payment_methods[pmidx].name">
                <label class="lpm-field">
                  <span class="lpm-field-label">Approved amount</span>
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].approved_amount"
                    type="number"
                    @keyup="set_validated(false)"
                    placeholder="Enter approved amount"
                    class="lpm-input"
                  ></b-input>
                </label>

                <label class="lpm-field">
                  <span class="lpm-field-label">Amount to apply</span>
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].amount"
                    type="number"
                    @keyup="set_validated(false)"
                    placeholder="Enter amount to apply"
                    class="lpm-input"
                  ></b-input>
                  <small
                    v-if="payment_methods[pmidx].approved_amount && parseFloat(payment_methods[pmidx].amount) > parseFloat(payment_methods[pmidx].approved_amount)"
                    class="lpm-hint is-danger"
                  >
                    <b-icon icon="exclamation-triangle-fill"></b-icon>
                    Applied amount exceeds approved amount
                  </small>
                </label>

                <label class="lpm-field">
                  <span class="lpm-field-label">Lease #</span>
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].lease_id"
                    @keyup="set_validated(false)"
                    placeholder="Enter lease number"
                    class="lpm-input"
                  ></b-input>
                </label>

                <label class="lpm-field">
                  <span class="lpm-field-label">Misc info</span>
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].misc_info"
                    placeholder="Additional notes"
                    class="lpm-input"
                  ></b-input>
                </label>
              </template>
            </template>

            <!-- STORE CREDIT -->
            <template v-if="payment_methods[pmidx].value == 'store_credit'">
              <label class="lpm-field">
                <span class="lpm-field-label">Original invoice #</span>
                <div class="lpm-input-row">
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].store_credit_invoice_id"
                    placeholder="Enter invoice ID"
                    class="lpm-input"
                  ></b-input>
                  <b-button class="lpm-btn-ghost lpm-check-btn" @click="apply_store_credit(pmidx)">
                    Check
                  </b-button>
                </div>
              </label>

              <template v-if="payment_methods[pmidx].available_amount">
                <label class="lpm-field">
                  <span class="lpm-field-label">Available credit</span>
                  <b-input
                    size="sm"
                    v-model="payment_methods[pmidx].available_amount"
                    disabled
                    class="lpm-input lpm-input--readonly"
                  ></b-input>
                </label>

                <label class="lpm-field">
                  <span class="lpm-field-label">Amount to apply</span>
                  <b-input
                    size="sm"
                    type="number"
                    v-model="payment_methods[pmidx].amount"
                    @keyup="set_validated(false)"
                    :max="payment_methods[pmidx].available_amount"
                    placeholder="Enter amount"
                    class="lpm-input"
                  ></b-input>
                  <small class="lpm-hint">Max: ${{ payment_methods[pmidx].available_amount }}</small>
                </label>
              </template>
            </template>
          </div>
        </div>
      </div>

      <!-- Validation section -->
      <div v-if="payment_methods.length > 0" class="lpm-validation">
        <div class="lpm-validation-grid">

          <div class="lpm-field">
            <span class="lpm-field-label">Last installment?</span>
            <b-form-radio-group
              v-model="is_last_payment"
              :options="last_payment_options"
              @change="validated = false"
              class="lpm-radio-group"
            ></b-form-radio-group>
          </div>

          <div v-if="is_last_payment" class="lpm-field">
            <span class="lpm-field-label">Delivery date</span>
            <b-form-datepicker
              v-model="delivery_date"
              required
              size="sm"
              class="lpm-datepicker"
              placeholder="Select delivery date"
            ></b-form-datepicker>
          </div>

          <div class="lpm-actions">
            <b-button class="lpm-btn-ghost" @click="printReceipt">
              <b-icon icon="printer"></b-icon> Print receipt
            </b-button>

            <b-button v-if="!validated" class="lpm-btn-orange" @click="validate">
              <b-icon icon="check2"></b-icon> Validate
            </b-button>
            <b-button v-else class="lpm-btn-teal" @click="submit_payments">
              <b-icon icon="check2-circle"></b-icon> Submit payments
            </b-button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import _ from 'underscore'
import moment from 'moment'
import global_vars from '@/utilities/global_vars'
import Backer from '@/components/globals/Backer'
import Requestor from '@/utilities/Requestor'

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: "LayAwayPaymentsModal",
  components: { Backer, Loading },
  props: ["viewdata", "financecompanies", "total_refunded_value"],
  data: function () {
    return {
      packet: {},
      invoice: {},
      payment_methods: [],
      payment_method_options: [
        { value: "cc", text: "💳 CREDIT CARD" },
        { value: "cash", text: "💵 CASH" },
        { value: "finance", text: "🏦 FINANCE" },
        { value: "store_credit", text: "🎫 STORE CREDIT" },
      ],
      finance_companies: [],
      is_last_payment: false,
      last_payment_options: [
        { value: true, text: "YES" },
        { value: false, text: "NO" }
      ],
      delivery_date: null,
      validated: false,
      sales_tax: 0.0,
    };
  },
  methods: {
    printReceipt() {
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      document.body.appendChild(printFrame);

      const receiptContent = this.generateReceiptHTML();

      const frameDoc = printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(receiptContent);
      frameDoc.close();

      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();

          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        }, 500);
      };
    },

    generateReceiptHTML() {
      let locationName = 'FURNITURE MECCA';
      let locationAddress = '';

      if (this.$parent && this.$parent.invoice_location_details) {
        locationName = this.$parent.invoice_location_details.short_name || locationName;
        locationAddress = this.$parent.invoice_location_details.address || '';
      }

      const currentDate = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      const invoiceNumber = this.invoice.invoice_id || 'N/A';
      const salesperson = (this.invoice.username || '').toUpperCase();
      const customerName = this.invoice.customer.fullname || 'Walk-in Customer';

      const invoiceTotal = parseFloat(this.invoice.total) || 0;
      const totalPaidSoFar = this.get_pms_total() || 0;
      const currentPaymentTotal = this.get_current_pms_total() || 0;
      const totalPaid = totalPaidSoFar + currentPaymentTotal;
      const balance = invoiceTotal - totalPaid;

      const existingPayments = this.invoice.payment_methods || [];
      const currentPayments = this.payment_methods || [];

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Layaway Payment Receipt - ${invoiceNumber}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: 72mm;
              margin: 0 auto;
              padding: 2mm;
              line-height: 1.3;
            }
            .header { text-align: center; font-weight: bold; margin-bottom: 3mm; }
            .store-name { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
            .store-location { font-size: 12px; margin-top: 1mm; }
            .receipt-badge {
              background: #000;
              color: #fff;
              padding: 2mm;
              text-align: center;
              font-weight: bold;
              margin: 2mm 0;
              border-radius: 3px;
            }
            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
            .info-row { display: flex; justify-content: space-between; margin: 1mm 0; }
            .info-label { font-weight: bold; }
            .payment-section { margin: 3mm 0; }
            .payment-header {
              font-weight: bold;
              border-bottom: 1px solid #000;
              padding-bottom: 1mm;
              margin-bottom: 2mm;
            }
            .payment-row { display: flex; justify-content: space-between; margin: 1mm 0; }
            .payment-detail { font-size: 10px; margin-left: 2mm; color: #666; }
            .total-row { display: flex; justify-content: space-between; margin: 2mm 0; font-weight: bold; }
            .grand-total {
              font-weight: bold;
              font-size: 14px;
              border-top: 2px solid #000;
              border-bottom: 2px solid #000;
              padding: 2mm 0;
              margin: 2mm 0;
            }
            .balance {
              font-weight: bold;
              font-size: 14px;
              background: #f0f0f0;
              padding: 2mm;
              margin: 2mm 0;
              border-radius: 3px;
            }
            .footer { text-align: center; margin-top: 5mm; font-size: 10px; }
            .thank-you { font-size: 14px; margin: 3mm 0; font-weight: bold; }
            .cut-line { text-align: center; margin-top: 5mm; font-size: 10px; }
            .final-payment {
              background: #ffe082;
              padding: 2mm;
              margin: 3mm 0;
              text-align: center;
              font-weight: bold;
              border-radius: 3px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="store-name">${locationName}</div>
            ${locationAddress ? `<div class="store-location">${locationAddress}</div>` : ''}
            <div class="store-location">${currentDate}</div>
          </div>

          <div class="receipt-badge">LAYAWAY PAYMENT RECEIPT</div>

          <div class="divider"></div>

          <div class="info-row">
            <span class="info-label">Invoice #:</span>
            <span>${invoiceNumber}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Salesperson:</span>
            <span>${salesperson}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Customer:</span>
            <span>${customerName}</span>
          </div>

          <div class="divider"></div>

          <div class="payment-section">
            <div class="payment-header">INVOICE SUMMARY</div>
            <div class="payment-row">
              <span>Original Total:</span>
              <span>$${invoiceTotal.toFixed(2)}</span>
            </div>
            <div class="payment-row">
              <span>Previously Paid:</span>
              <span>$${totalPaidSoFar.toFixed(2)}</span>
            </div>
          </div>

          ${existingPayments.length > 0 ? `
            <div class="payment-section">
              <div class="payment-header">PREVIOUS PAYMENTS</div>
              ${existingPayments.map(pm => `
                <div class="payment-row">
                  <span>${this.format_ts(pm.ts)}</span>
                  <span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span>
                </div>
                <div class="payment-row" style="font-size: 10px; color: #666;">
                  <span>${(pm.value || '').toUpperCase()}</span>
                  <span>${pm.name || ''}</span>
                </div>
              `).join('')}
            </div>
          ` : ''}

          ${currentPayments.length > 0 ? `
            <div class="payment-section">
              <div class="payment-header">CURRENT PAYMENT${currentPayments.length > 1 ? 'S' : ''}</div>
              ${currentPayments.map(pm => `
                <div class="payment-row">
                  <span>${(pm.value || '').toUpperCase()}${pm.layaway_sls_tax ? ' (TAX)' : ''}</span>
                  <span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span>
                </div>
                ${pm.value === 'finance' && pm.approved_amount ? `
                  <div class="payment-row" style="font-size: 10px; color: #666;">
                    <span>Approved: $${(parseFloat(pm.approved_amount) || 0).toFixed(2)}</span>
                    ${pm.lease_id ? `<span>Lease: ${pm.lease_id}</span>` : ''}
                  </div>
                ` : ''}
              `).join('')}
              <div class="payment-row" style="margin-top: 2mm; font-weight: bold;">
                <span>Current Total:</span>
                <span>$${currentPaymentTotal.toFixed(2)}</span>
              </div>
            </div>
          ` : ''}

          <div class="divider"></div>

          <div class="grand-total total-row">
            <span>TOTAL PAID TO DATE:</span>
            <span>$${totalPaid.toFixed(2)}</span>
          </div>

          <div class="balance total-row">
            <span>REMAINING BALANCE:</span>
            <span>$${balance.toFixed(2)}</span>
          </div>

          ${this.is_last_payment ? `
            <div class="final-payment">
              ⚠️ FINAL PAYMENT - INVOICE PAID IN FULL
              ${this.delivery_date ? `<br>Delivery Date: ${this.delivery_date}` : ''}
            </div>
          ` : ''}

          <div class="footer">
            <div class="thank-you">THANK YOU FOR YOUR PAYMENT!</div>
            <div>${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
          </div>

          <div class="cut-line">- - - - - - - - - - - - - - - - - - - -</div>
        </body>
        </html>
      `;
    },

    apply_store_credit: function (pmidx) {
      let payment_methods = this.payment_methods;
      let store_credit_invoice_id = payment_methods[pmidx].store_credit_invoice_id;

      if (!store_credit_invoice_id) {
        this.$vex.dialog.alert({ message: "Please specify valid invoice id to find out its store credit balance." });
        return;
      }

      let path = "/api/sales/get_available_credit/";
      let data = { invoice_id: store_credit_invoice_id };
      let that = this;

      let s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          if (response.data.result.length > 0) {
            that.payment_methods[pmidx].available_amount = response.data.result[0].available_amount;
            that.payment_methods[pmidx].name = store_credit_invoice_id;

            if (response.data.result[0].available_amount > 0) {
              that.$bvToast.toast("Store credit retrieved.", { title: "Success", autoHideDelay: 1500, variant: "success" });
            } else {
              that.$bvToast.toast("No Store credit available for " + store_credit_invoice_id, { title: "Notice", autoHideDelay: 1500, variant: "warning" });
            }
          } else {
            that.$bvToast.toast("No store credit found with the given invoice.", { title: "Not found", autoHideDelay: 1500, variant: "warning" });
          }
        }
      };

      let f = function (error) {
        console.log(error);
        that.$bvToast.toast("Error occurred while applying store credit.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };

      Requestor.postAsyncP(path, data, this, s, f);
    },

    reset_key_metrics: function () {
      this.is_last_payment = false;
      this.sales_tax = 0.0;
      this.validated = false;
    },

    format_ts: function (ts) {
      return moment(ts).format("MM/DD/YYYY hh:mm A");
    },

    round_right: function (value) {
      return parseFloat(parseFloat(value).toFixed(2));
    },

    set_validated(value) {
      this.validated = value;
    },

    get_current_pms_total: function () {
      let pms = this.payment_methods;
      let total_paid = 0.0;
      for (var i = 0; i < pms.length; i++) {
        if (pms[i].layaway_sls_tax) continue;
        let amount = parseFloat(pms[i].amount) || 0.0;
        total_paid += amount;
      }
      return total_paid;
    },

    get_pms_total: function () {
      let pms = this.invoice.payment_methods;
      let total_paid = 0.0;
      for (var i = 0; i < pms.length; i++) {
        let amount = parseFloat(pms[i].amount) || 0.0;
        total_paid += amount;
      }
      return total_paid;
    },

    add_payment_method: function () {
      let idx = 0;
      if (this.payment_methods.length > 0) {
        idx = this.payment_methods[this.payment_methods.length - 1].idx + 1;
      } else {
        if (this.invoice.payment_methods.length > 0) {
          this.invoice.payment_methods.sort((a, b) => a.idx - b.idx);
          idx = this.invoice.payment_methods[this.invoice.payment_methods.length - 1].idx + 1;
        }
      }

      let new_payment_method = {
        idx: idx,
        value: null,
        amount: null,
        approved_amount: null,
        properties: {},
        name: null,
        available_amount: 0.0,
        store_credit_invoice_id: null,
        charged_tax_itself: false,
        lease_id: null,
        misc_info: null,
        new: true,
      };

      this.payment_methods.push(new_payment_method);
      this.validated = false;
      this.sales_tax = 0.0;
      this.is_last_payment = false;
    },

    removePaymentMethod: function (pmidx) {
      this.payment_methods.splice(pmidx, 1);
      this.validated = false;
      this.sales_tax = 0.0;
      this.is_last_payment = false;
    },

    validate_payment_methods: function (payment_methods) {
      let errors = [];
      let paid_so_far = this.roundTo((this.get_pms_total() + this.get_current_pms_total() || 0.0), 2);

      for (var i = 0; i < payment_methods.length; i++) {
        if (!payment_methods[i].value || !payment_methods[i].amount || !parseFloat(payment_methods[i].amount) || parseFloat(payment_methods[i].amount) <= 0) {
          errors.push("One or more invalid payment amounts is present. Please indicate payment method and a proper numerical amount.");
        }

        if (payment_methods[i].value == "cash") {
          payment_methods[i].name = "N/A";
        }

        if (payment_methods[i].value == "cc") {
          if (!(payment_methods[i].name)) {
            errors.push("All credit card payments should include their last 4 digits for identity.");
          }
          if (!(parseInt(payment_methods[i].name))) {
            errors.push("All credit card payments should include their valid last 4 digits.");
          }
          if ((payment_methods[i].name || "").replaceAll(/\s/g, "").length < 4) {
            errors.push("All credit card payments should include their valid last 4 digits without spaces.");
          }
        }

        if (payment_methods[i].value == "finance") {
          if (!(payment_methods[i].lease_id)) {
            errors.push("All finance related sales should include their lease/finance id.");
          }
          if (!(payment_methods[i].name)) {
            errors.push("All finance related sales should include their lease/finance company name.");
          }
          if (payment_methods[i].approved_amount && parseFloat(payment_methods[i].approved_amount) < 0) {
            errors.push("Approved amount cannot be negative.");
          }
          if (payment_methods[i].approved_amount && parseFloat(payment_methods[i].amount) > parseFloat(payment_methods[i].approved_amount)) {
            errors.push("Applied amount cannot exceed approved amount for finance payments.");
          }
        }

        if (payment_methods[i].value == "store_credit") {
          if (parseFloat(payment_methods[i].amount) > parseFloat(payment_methods[i].available_amount)) {
            errors.push("All the store credit amounts should be less or equal to available credit amount.");
          }
          if (isNaN(parseFloat(payment_methods[i].available_amount)) || parseFloat(payment_methods[i].available_amount) == 0.0) {
            errors.push("No store credit can be applied with given information.");
          }
        }

        let amount = parseFloat(payment_methods[i].amount);
        if (!amount) {
          errors.push("Please provide valid amounts with all the payment methods.");
        }
      }

      if (paid_so_far > this.invoice.total) {
        errors.push("Total payments cannot exceed the existing balance.");
      }

      return errors;
    },

    validate: function () {
      let errors = this.validate_payment_methods(this.payment_methods);
      if (errors.length > 0) {
        this.$vex.dialog.alert({ message: errors[0] });
        return;
      } else {
        let paid_so_far = this.get_current_pms_total() + this.get_pms_total();
        let invoice_total = this.invoice.total - this.total_refunded_value;

        if (this.is_last_payment) {
          let sales_tax = this.get_total_tax();
          if (paid_so_far != invoice_total) {
            this.$vex.dialog.alert({ message: "LAST INSTALLMENT SHOULD SET THE BALANCE TO 0." });
            return;
          } else {
            this.sales_tax = Number(sales_tax) || 0;
            this.payment_methods[0].amount = this.roundTo(parseFloat(this.payment_methods[0].amount), 2);

            let idx = 0;
            if (this.payment_methods.length > 0) {
              idx = this.payment_methods[this.payment_methods.length - 1].idx + 1;
            } else {
              if (this.invoice.payment_methods.length > 0) {
                this.invoice.payment_methods.sort((a, b) => a.idx - b.idx);
                idx = this.invoice.payment_methods[this.invoice.payment_methods.length - 1].idx + 1;
              }
            }

            if (this.sales_tax > 0.0) {
              if (this.payment_methods.filter(function (d) { return d.layaway_sls_tax == true }).length == 0) {
                this.payment_methods.push({
                  layaway_sls_tax: true,
                  idx: idx,
                  value: 'cash',
                  amount: this.roundTo(parseFloat(this.sales_tax), 2),
                  properties: {},
                  name: null,
                  available_amount: 0.0,
                  store_credit_invoice_id: null,
                  charged_tax_itself: false,
                  new: true,
                });
              }
            }
            this.validated = true;
          }
        } else {
          if (paid_so_far == invoice_total) {
            this.$vex.dialog.alert({ message: "LOOKS LIKE THIS IS THE LAST INSTALLMENT. PLEASE INDICATE IT BELOW." });
            this.validated = false;
          } else {
            this.validated = true;
          }
        }
      }
    },

    roundTo: function (n, digits) {
      var negative = false;
      if (digits === undefined) digits = 0;
      if (n < 0) { negative = true; n = n * -1; }
      var multiplicator = Math.pow(10, digits);
      n = parseFloat((n * multiplicator).toFixed(11));
      n = (Math.round(n) / multiplicator).toFixed(digits);
      if (negative) n = (n * -1).toFixed(digits);
      return n;
    },

    get_total_tax: function () {
      let old_payment_methods = this.invoice.payment_methods;
      let payment_methods = this.payment_methods;
      let sales_tax = 0.0;

      for (var i = 0; i < old_payment_methods.length; i++) {
        let opm = old_payment_methods[i];
        if (opm.layaway_sls_tax == true) continue;

        if (opm.value == "finance") {
          let opm_details = this.finance_companies.filter(function (d) { return opm.name == d.value })[0];
          if (!(opm_details.company_charges_tax_in_states.includes(this.invoice.sold_from_state))) {
            sales_tax += ((parseFloat(opm.amount) || 0.0) * this.invoice.tax_perc);
          } else {
            this.invoice.payment_methods[i].charged_tax_itself = true;
          }
        } else {
          if (["store_credit", "cc", "cash"].includes(opm.value)) {
            sales_tax += (parseFloat(opm.amount) || 0.0) * this.invoice.tax_perc;
          }
        }
      }

      for (var i = 0; i < payment_methods.length; i++) {
        let pm = payment_methods[i];
        if (pm.layaway_sls_tax == true) continue;

        if (pm.value == "finance") {
          let pm_details = this.finance_companies.filter(function (d) { return pm.name == d.value })[0];
          if (!(pm_details.company_charges_tax_in_states.includes(this.invoice.sold_from_state))) {
            sales_tax += (parseFloat(pm.amount) || 0.0) * this.invoice.tax_perc;
          } else {
            this.payment_methods[i].charged_tax_itself = true;
          }
        } else {
          if (["store_credit", "cc", "cash"].includes(pm.value)) {
            sales_tax += (parseFloat(pm.amount) || 0.0) * this.invoice.tax_perc;
          }
        }
      }

      return (this.roundTo(sales_tax, 2) || 0.0);
    },

    submit_payments: function () {
      let payment_methods = this.payment_methods;
      let old_payment_methods = JSON.parse(JSON.stringify(this.invoice.payment_methods));
      let final_payment_methods = old_payment_methods.concat(payment_methods);

      let that = this;
      let data = {
        invoice_id: this.invoice.invoice_id,
        delivery_date: this.delivery_date,
        sales_tax: this.sales_tax,
        is_last_payment: this.is_last_payment,
        payment_methods: final_payment_methods
      };

      if (this.is_last_payment) {
        if (this.validated) {
          if (this.sales_tax > 0) {
            let sales_tax_payment_method = this.payment_methods.filter(function (d) { return d.layaway_sls_tax == true });
            if (sales_tax_payment_method.length > 0) {
              sales_tax_payment_method = sales_tax_payment_method[0];
              if (sales_tax_payment_method.value == 'cc') {
                if ((sales_tax_payment_method.name || "").replaceAll(/\s/g, "").length < 4) {
                  this.$vex.dialog.alert({ message: "PLEASE PROVIDE 4 DIGITS OF CC." });
                  return;
                }
              }
            } else {
              this.$vex.dialog.alert({ message: "NO PAYMENT METHOD SPECIFIED FOR SALES TAX." });
              return;
            }
          }
        } else {
          this.$vex.dialog.alert({ message: "ERROR IN PAYMENT METHODS. PLEASE TRY AGAIN." });
          return;
        }
      }

      let path = "/api/sales/add_layaway_payments/";
      let s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.$bvToast.toast("ADDED PAYMENTS.", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.$emit("added-layaway-payments", that.items);
        }
      };
      let f = function (error) {
        that.$bvToast.toast("Error updating.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
      };
      Requestor.postAsyncP(path, data, this, s, f);

      return;
    }
  },
  created: function () {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    this.invoice = JSON.parse(JSON.stringify(this.viewdata));
    this.total_refunded_value = JSON.parse(JSON.stringify(this.total_refunded_value));
    this.finance_companies = JSON.parse(JSON.stringify(this.financecompanies));
  }
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.lpm-page {
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 16px;
  padding: 4px 0;
}

/* ===================================================== */
/* INTRO STRIP                                           */
/* ===================================================== */
.lpm-intro {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 18px;
  margin-bottom: 14px;
}
.lpm-intro-label {
  font-size: 12px;
  font-weight: 700;
  color: #736C63;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.lpm-intro-title {
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 700;
  margin-top: 2px;
}
.lpm-intro-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.lpm-tag {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 700;
  padding: 5px 13px; border-radius: 999px;
  border: 1px solid #E5E1DA;
  background: #F7F5F2;
  color: #4A4540;
}
.lpm-tag.is-orange { background: #FFF1E4; color: #D4620F; border-color: #F5CFAE; }
.lpm-tag.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }

/* ===================================================== */
/* CARD / SECTIONS                                       */
/* ===================================================== */
.lpm-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 18px 20px;
  margin-bottom: 14px;
}

.lpm-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.lpm-section-title { display: flex; align-items: center; gap: 12px; }
.lpm-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: #F1EEE9; color: #4A4540;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.lpm-icon.is-orange { background: #FFF1E4; color: #D4620F; }
.lpm-section-label {
  font-family: 'Sora', sans-serif;
  font-size: 17px; font-weight: 700;
}
.lpm-section-sub { font-size: 13.5px; color: #736C63; margin-top: 2px; }

/* ===================================================== */
/* EMPTY                                                 */
/* ===================================================== */
.lpm-empty {
  text-align: center;
  padding: 40px 20px;
  background: #FAF9F7;
  border: 2px dashed #D6CFC4;
  border-radius: 14px;
}
.lpm-empty-icon {
  width: 68px; height: 68px; border-radius: 20px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  margin-bottom: 12px; font-size: 30px;
}
.lpm-empty h3 {
  font-family: 'Sora', sans-serif;
  font-size: 18px; font-weight: 700;
  margin: 0 0 4px;
}
.lpm-empty p { color: #736C63; margin: 0; font-size: 15px; }

/* ===================================================== */
/* TABLE                                                 */
/* ===================================================== */
.lpm-table-wrap {
  overflow-x: auto;
  border: 1px solid #E5E1DA;
  border-radius: 12px;
  margin-bottom: 16px;
}
.lpm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  min-width: 480px;
}
.lpm-table thead th {
  background: #221F1C;
  color: #fff;
  font-family: 'Sora', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px 14px;
  text-align: left;
  border-bottom: 3px solid #EE7A22;
  white-space: nowrap;
}
.lpm-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: middle;
  color: #221F1C;
}
.lpm-table tbody tr:last-child td { border-bottom: 0; }
.lpm-table tbody tr:hover td { background: #FAF9F7; }

.lpm-td--num { text-align: right; }
.lpm-date-cell {
  font-family: 'Menlo', monospace;
  font-size: 13.5px;
  font-weight: 700;
  color: #4A4540;
  white-space: nowrap;
}
.lpm-method-chip {
  display: inline-flex; align-items: center;
  font-size: 12.5px; font-weight: 800;
  padding: 4px 10px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
  margin-right: 8px;
  white-space: nowrap;
}
.lpm-method-chip.is-cc           { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.lpm-method-chip.is-cash         { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.lpm-method-chip.is-finance      { background: #EEE6F9; color: #5E37A3; border-color: #D4C0F0; }
.lpm-method-chip.is-store_credit { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }

.lpm-method-detail {
  font-size: 13px;
  color: #736C63;
  font-weight: 600;
}
.lpm-amount {
  font-family: 'Sora', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #0F6E6B;
  font-variant-numeric: tabular-nums;
}

/* ===================================================== */
/* SUMMARY CARDS                                         */
/* ===================================================== */
.lpm-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.lpm-summary-card {
  background: #F7F5F2;
  border: 1px solid #E5E1DA;
  border-left: 5px solid transparent;
  border-radius: 12px;
  padding: 12px 16px;
}
.lpm-summary-card.is-total   { border-left-color: #5E37A3; }
.lpm-summary-card.is-total   .lpm-sum-value { color: #5E37A3; }
.lpm-summary-card.is-paid    { border-left-color: #0F6E6B; }
.lpm-summary-card.is-paid    .lpm-sum-value { color: #0F6E6B; }
.lpm-summary-card.is-current { border-left-color: #C98A12; }
.lpm-summary-card.is-current .lpm-sum-value { color: #8A5E09; }
.lpm-summary-card.is-balance { border-left-color: #C93C30; }
.lpm-summary-card.is-balance .lpm-sum-value { color: #C93C30; }
.lpm-summary-card.is-balance.is-positive { border-left-color: #0F6E6B; }
.lpm-summary-card.is-balance.is-positive .lpm-sum-value { color: #0F6E6B; }

.lpm-sum-label {
  font-size: 12px;
  font-weight: 700;
  color: #736C63;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.lpm-sum-value {
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #221F1C;
}

@media (max-width: 900px) {
  .lpm-summary { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 480px) {
  .lpm-summary { grid-template-columns: 1fr; }
}

/* ===================================================== */
/* TAX ALERT                                             */
/* ===================================================== */
.lpm-tax-alert {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  padding: 12px 16px;
  background: #FFF4DC;
  border: 1px solid #F2DBA5;
  border-left: 5px solid #C98A12;
  border-radius: 12px;
  color: #7A520A;
  font-size: 15px;
}
.lpm-tax-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: #FFE2C7; color: #B5520B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.lpm-tax-note {
  display: inline-block;
  margin-top: 2px;
  font-size: 12.5px;
  background: rgba(0, 0, 0, 0.08);
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
}

/* ===================================================== */
/* BUTTONS                                               */
/* ===================================================== */
.lpm-btn-primary.btn,
.lpm-btn-ghost.btn,
.lpm-btn-orange.btn,
.lpm-btn-teal.btn {
  border-radius: 10px !important;
  font-weight: 700;
  font-size: 14.5px;
  padding: 9px 16px;
  display: inline-flex; align-items: center; gap: 7px;
  box-shadow: none !important;
}
.lpm-btn-primary.btn {
  background: #EE7A22 !important;
  border: 1.5px solid #EE7A22 !important;
  color: #fff !important;
}
.lpm-btn-primary.btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }
.lpm-btn-orange.btn {
  background: #C98A12 !important;
  border: 1.5px solid #C98A12 !important;
  color: #fff !important;
}
.lpm-btn-orange.btn:hover:not(:disabled) { background: #A5720E !important; border-color: #A5720E !important; }
.lpm-btn-teal.btn {
  background: #0F6E6B !important;
  border: 1.5px solid #0F6E6B !important;
  color: #fff !important;
}
.lpm-btn-teal.btn:hover:not(:disabled) { background: #0B5552 !important; border-color: #0B5552 !important; }
.lpm-btn-ghost.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
}
.lpm-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }

/* ===================================================== */
/* NEW PAYMENTS GRID                                     */
/* ===================================================== */
.lpm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.lpm-pay-card {
  background: #fff;
  border: 1.5px solid #E5E1DA;
  border-radius: 14px;
  overflow: hidden;
  animation: lpmSlideIn 0.3s ease-out;
}
@keyframes lpmSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

.lpm-pay-card.is-tax {
  border-color: #F2DBA5;
  background: #FFFCF2;
}

.lpm-pay-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #F7F5F2;
  border-bottom: 1px solid #E5E1DA;
}
.lpm-pay-card.is-tax .lpm-pay-head { background: #FFF4DC; border-bottom-color: #F2DBA5; }

.lpm-pay-num {
  display: flex; align-items: center; gap: 8px;
  font-family: 'Sora', sans-serif;
  font-size: 14.5px; font-weight: 700;
  color: #221F1C;
}
.lpm-pay-num .b-icon { color: #EE7A22; }

.lpm-pay-head-right { display: flex; align-items: center; gap: 8px; }

.lpm-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 700;
  padding: 3px 10px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
}
.lpm-chip.is-warn { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }

.lpm-remove-btn {
  background: transparent !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 8px !important;
  color: #C93C30 !important;
  padding: 4px 8px !important;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: none !important;
  line-height: 1;
}
.lpm-remove-btn:hover { background: #FBE9E7 !important; border-color: #F3C9C3 !important; }

.lpm-pay-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ===================================================== */
/* FIELDS                                                */
/* ===================================================== */
.lpm-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.lpm-field-label {
  font-size: 12.5px;
  font-weight: 800;
  color: #736C63;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.lpm-hint {
  font-size: 12.5px;
  color: #736C63;
  margin-top: 2px;
  display: flex; align-items: center; gap: 5px;
}
.lpm-hint.is-danger { color: #C93C30; font-weight: 700; }
.lpm-input-row { display: flex; gap: 8px; }
.lpm-input-row .lpm-input { flex: 1; }
.lpm-check-btn.btn { white-space: nowrap; }

/* Inputs */
.lpm-input >>> .form-control,
.lpm-select >>> .custom-select {
  height: 42px;
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  background: #F7F5F2;
  color: #221F1C;
  font-size: 15px;
  font-weight: 600;
  padding: 0 12px;
  box-shadow: none !important;
}
.lpm-input >>> .form-control:focus,
.lpm-select >>> .custom-select:focus {
  border-color: #EE7A22;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.18) !important;
}
.lpm-input--readonly >>> .form-control {
  background: #F1EEE9;
  color: #736C63;
}

/* ===================================================== */
/* VALIDATION SECTION                                    */
/* ===================================================== */
.lpm-validation {
  margin-top: 6px;
  padding: 16px 18px;
  background: #F7F5F2;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
}
.lpm-validation-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 18px;
}
.lpm-radio-group {
  display: flex;
  gap: 16px;
  padding-top: 4px;
}
.lpm-radio-group >>> .custom-control { margin-right: 8px; font-size: 15px; font-weight: 700; }

.lpm-datepicker >>> .b-form-datepicker,
.lpm-datepicker >>> .form-control {
  height: 42px;
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  background: #fff;
}

.lpm-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

@media (max-width: 767px) {
  .lpm-actions { margin-left: 0; width: 100%; }
  .lpm-actions .btn { flex: 1; }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/LayAwayPaymentsModal.vue